import Service, { service } from '@ember/service'
import { isPresent, isEmpty } from '@ember/utils'
import { isNotFoundError } from 'teacher-ui-client/errors/http-error'
import { min, max } from 'date-fns'
import { SchoolClassOptionTypes } from 'teacher-ui-client/components/base/select/school-class/options'
import type Store from '@ember-data/store'
import type SessionService from 'teacher-ui-client/services/session'
import type Assignment from 'teacher-ui-client/models/assignment'
import type Student from 'teacher-ui-client/models/student'
import type { SortDirection } from '@blakeelearning/data-tables/utils/sorting'
import type DataTransformer from '@blakeelearning/data-tables/services/data-transformer'
import type ReadAloudReview from 'teacher-ui-client/models/read-aloud-review'
import type StudentEventsService from 'teacher-ui-client/services/student-events'
import type { AssignmentScoresData } from 'teacher-ui-client/utils/student-events/fetchers/assignment-scores'
import type { AssignmentsScope } from 'teacher-ui-client/reporting/ui-scope/utils'
import { hasId } from 'teacher-ui-client/utils/type-guards'

type AssignmentTabScope = TabScope.CurrentAssignments | TabScope.PastAssignments
type StudentTabScope = TabScope.AllStudents | TabScope.MyStudents

export enum TabScope {
  CurrentAssignments = 'current',
  PastAssignments = 'past',
  MyStudents = 'my-students',
  AllStudents = 'all-students',
}

interface ForAssignmentsArgs {
  sortingAndPagination: { page: number; perPage: number; sortDirection: string; sortColumn: string }
  product: LegacyProductRe | LegacyProductRex | LegacyProductMs | LegacyProductWl | LegacyProductFp
  tabScope: AssignmentTabScope
}

interface ForStudentsArgs {
  currentTeacherId: string
  product: LegacyProductRe | LegacyProductRex | LegacyProductMs | LegacyProductWl | LegacyProductFp
  selectedSchoolClassId: string | SchoolClassOptionTypes
  students: Student[]
  limit: number
  tabScope: StudentTabScope
}

interface FetchAssignmentsArgs {
  product: LegacyProductRe | LegacyProductRex | LegacyProductMs | LegacyProductWl | LegacyProductFp
  perPage: number
  page: number
  sortColumn: string
  sortDirection: string
  tabScope: AssignmentTabScope
}

interface FetchAssignmentsForStudentsArgs {
  product: LegacyProductRe | LegacyProductRex | LegacyProductMs | LegacyProductWl | LegacyProductFp
  studentIds?: string[]
  /**
   * the limit of the number of assignment records fetched. Default is 30
   */
  limit?: number
}

function deserializeSortDirection(value: unknown): SortDirection {
  return value === 'desc' ? value : 'asc'
}

/**
 * Extracts the first and last dates of the date range that an array of assignments spans. Returns a 2 element tuple
 * (ie array of two items) with the first and last dates, respectively
 */
function dateRangeLimitsFromAssignments(assignments: Assignment[]): [Date, Date] {
  const assignmentDates = assignments.reduce<Date[]>(
    (acc, assignment) => [...acc, assignment.startAt, assignment.endAt],
    [],
  )
  return [min(assignmentDates), max(assignmentDates)]
}

/**
 * Service to provide assignments manage sections to the access to
 * apis and data manipulation related functionality that they need.
 *
 * We need to use such a service because of reuse across several routes,
 * as well as satisfying the requirement to have a location to share code
 * related to this data, and it also lets us test the code in a more
 * isolated context.
 */

export default class AssignmentsManageDataService extends Service {
  @service store!: Store

  @service dataTransformer!: DataTransformer

  @service studentEvents!: StudentEventsService

  @service session!: SessionService

  /**
   * Public API for fetching assignments related data for
   * model hook in the route on 'current' tab in assignments
   * Please see docs for `forAssignments`
   */
  async forCurrentAssignments(params: Omit<ForAssignmentsArgs, 'tabScope'>) {
    const adjustedParams: ForAssignmentsArgs = { ...params, tabScope: TabScope.CurrentAssignments }
    return this.forAssignments(adjustedParams)
  }

  /**
   * Public API for fetching assignments related data for
   * model hook in the route on 'past' tab in assignments
   * Please see docs for `forAssignments`
   */
  async forPastAssignments(params: Omit<ForAssignmentsArgs, 'tabScope'>) {
    const adjustedParams: ForAssignmentsArgs = { ...params, tabScope: TabScope.PastAssignments }
    return this.forAssignments(adjustedParams)
  }

  /**
   * Public API for fetching assignments related data for
   * model hook in the route on 'my-students' tab in assignments
   * Please see docs for `forStudents`
   */
  async forMyStudents(params: Omit<ForStudentsArgs, 'tabScope'>) {
    const adjustedParams: ForStudentsArgs = { ...params, tabScope: TabScope.MyStudents }
    return this.forStudents(adjustedParams)
  }

  /**
   * Public API for fetching assignments related data for
   * model hook in the route on 'all-students' tab in assignments
   * Please see docs for `forStudents`
   */
  async forAllStudents(params: Omit<ForStudentsArgs, 'tabScope'>) {
    const adjustedParams: ForStudentsArgs = { ...params, tabScope: TabScope.AllStudents }
    return this.forStudents(adjustedParams)
  }

  /**
   * Provide the main 'current' and 'past' assignments tabs with the data they need.
   */
  private async forAssignments({
    sortingAndPagination: { page, perPage, sortDirection, sortColumn },
    product,
    tabScope,
  }: ForAssignmentsArgs) {
    const { assignments, totalAssignments } = await this.fetchAssignments({
      product,
      perPage,
      page,
      sortColumn,
      sortDirection,
      tabScope,
    })
    const [assignmentScores, readAloudReviews] = await Promise.all([
      this.fetchAssignmentScores({ product, assignments }),
      this.fetchReadAloudReviews({ product, assignments }),
    ])

    const response = {
      assignmentScores,
      readAloudReviews,
      assignments,
      totalAssignments,
      product,
      assignmentsPerPage: perPage,
      sortingAndPagination: { page, perPage, sortDirection, sortColumn },
    }
    return response
  }

  /**
   * Provide the main 'my-students' and 'all-students' assignments tabs with the data they need.
   */
  private async forStudents({
    currentTeacherId,
    product,
    selectedSchoolClassId,
    students: allStudents,
    limit,
    tabScope,
  }: ForStudentsArgs) {
    const pipeline = [
      this.filterStudentsBasedOnTabScope({ currentTeacherId, tabScope }),
      this.filterStudentsBasedOnClass({ selectedSchoolClassId }),
    ]

    const { items: students } = await this.dataTransformer.transform<Student, Student>(allStudents, pipeline)

    const assignments = await this.fetchAssignmentsForStudents({
      product,
      limit,
      studentIds: students.filter(hasId).map((student) => student.id),
    })

    const response = {
      students,
      allStudents,
      product,
      assignments,
    }
    return response
  }

  /**
   * Fetches the assignments from the backend using the Ember Data query interface for
   * Assignment. We do server-side pagination and sorting.
   */
  private async fetchAssignments({
    product,
    perPage,
    page,
    sortColumn,
    sortDirection,
    tabScope,
  }: FetchAssignmentsArgs): Promise<{ assignments: Assignment[]; totalAssignments?: number }> {
    const paginationAndOrderingOptions = {
      limit: perPage,
      offset: (page - 1) * perPage,
      order_by: { [sortColumn]: deserializeSortDirection(sortDirection) },
    }

    const assignmentsQuery = await this.store.query<Assignment>('assignment', {
      ...paginationAndOrderingOptions,
      prefix: `teacher/${product}`,
      suffix: tabScope === TabScope.CurrentAssignments ? 'current' : 'past',
    })
    const assignments = assignmentsQuery.slice()
    const assignmentsMeta = (assignmentsQuery as unknown as { meta?: { total?: number } })?.meta
    return { assignments, totalAssignments: assignmentsMeta?.total }
  }

  private async fetchAssignmentsForStudents({
    product,
    studentIds,
    limit,
  }: FetchAssignmentsForStudentsArgs): Promise<Assignment[]> {
    if (isEmpty(studentIds)) return [] as Assignment[]

    return (
      await this.store.query<Assignment>('assignment', {
        prefix: `students/${product}`,
        suffix: 'current',
        student_ids: studentIds,
        limit,
      })
    ).slice()
  }

  /**
   * Fetches the assignment scores data from student_events API
   */
  private async fetchAssignmentScores({
    product,
    assignments,
  }: {
    product: LegacyProductRe | LegacyProductRex | LegacyProductMs | LegacyProductWl | LegacyProductFp
    assignments: Assignment[]
  }): Promise<AssignmentScoresData> {
    if (isPresent(assignments)) {
      const scope: AssignmentsScope = { scope: 'assignments', ids: assignments.filter(hasId).map((a) => a.id) }
      const [fromDate, toDate] = dateRangeLimitsFromAssignments(assignments)
      const period = {
        start: fromDate,
        end: toDate,
      }
      try {
        const scores = await this.studentEvents.assignmentScores(product, scope, period)
        if (scores) return scores
      } catch (e: unknown) {
        // endpoint gives 404 when assignments haven't been actioned yet - treat as no data, rethrow everything else
        const error = (e as { errors?: [unknown] })?.errors?.[0]
        const isResourceNotFound = isNotFoundError(error)
        if (!isResourceNotFound) {
          throw e
        }
      }
    }

    return []
  }

  /**
   * Fetches the assignment read aloud reviews data
   */
  private async fetchReadAloudReviews({
    product,
    assignments,
  }: {
    product: LegacyProductRe | LegacyProductRex | LegacyProductMs | LegacyProductWl | LegacyProductFp
    assignments: Assignment[]
  }): Promise<ReadAloudReview[]> {
    // Only rex assignments currently have read aloud reviews
    if (product !== 'rex') return []
    if (!assignments.length) return []

    const reviews = await this.store.query<ReadAloudReview>('read-aloud-review', {
      filter: { assignment_uuids: assignments.map((a) => a.id) },
      // @ts-expect-error - include types not correct
      include: 'student',
    })
    return reviews.slice()
  }

  /**
   * Builds a pipeline function that filters the list of students depending on which tab is
   * currently selected; For 'my-students', it's students of the currently logged in teacher, otherwise all.
   */
  private filterStudentsBasedOnTabScope({
    currentTeacherId,
    tabScope,
  }: {
    currentTeacherId: string
    tabScope: TabScope
  }) {
    const builtOperation = this.dataTransformer.itemsOperation<Student, Student>((students) => {
      if (tabScope === TabScope.MyStudents) {
        return students.filter((student) => student.allTeachers.some((t) => t.id === currentTeacherId))
      } else {
        return students
      }
    })
    return builtOperation
  }

  /**
   * Builds a pipeline function that filters the list of students depending on what their
   * school class ID is.
   */
  private filterStudentsBasedOnClass({
    selectedSchoolClassId,
  }: {
    selectedSchoolClassId?: string | SchoolClassOptionTypes
  }) {
    const builtOperation = this.dataTransformer.itemsOperation<Student, Student>((students) => {
      if (isEmpty(selectedSchoolClassId) || selectedSchoolClassId === SchoolClassOptionTypes.All) return students

      if (selectedSchoolClassId === SchoolClassOptionTypes.My) {
        return students.filter((s) => s.schoolClasses.some((sc) => sc.teachers.includes(this.session.currentTeacher)))
      }

      return students.filter((student) => student.schoolClasses.some((sc) => sc.id === selectedSchoolClassId))
    })
    return builtOperation
  }
}

declare module '@ember/service' {
  interface Registry {
    'assignments-admin/manage-data': AssignmentsManageDataService
  }
}
